import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import makeStyles from '@mui/styles/makeStyles';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { FormattedMessage } from 'react-intl';
import DeleteIcon from '@mui/icons-material/Delete';
import { ApolloError } from '@apollo/client';
import { CircularProgress, IconButton, Typography } from '@mui/material';
import { CustomLabel } from 'modules/timesheet-labels/labelSchema';
import { useMessage } from 'modules/intl';
import { ProgressButton } from 'modules/common';
import { useDialogState } from 'modules/common/hooks';
import { useDeleteCustomLabel, useUpdateCustomLabel } from '../hooks';

const useStyles = makeStyles((theme) => ({
  dialogAction: {
    marginLeft: theme.spacing(3),
    marginBottom: theme.spacing(1)
  },
  cancelButton: {
    fontWeight: theme.typography.fontWeightMedium
  },
  addButton: {
    fontWeight: theme.typography.fontWeightMedium,
    marginRight: theme.spacing(2),
    padding: theme.spacing(0.75, 3)
  },
  title: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: theme.palette.grey[600],
    ...theme.typography.h6
  },
  edit: {
    marginTop: theme.spacing(0.5)
  },
  delete: {
    color: theme.palette.error.main,
    padding: theme.spacing(0)
  }
}));

type EditKeyDialogProps = {
  open: boolean;
  onEditComplete: (updatedLabel?: CustomLabel) => void;
  onDeleteComplete: (id?: string) => void;
  onClose: () => void;
  label: CustomLabel;
};

const inputProps = { 'data-testid': 'edit-custom-key-input' };
export const EditKeyDialog: React.FC<EditKeyDialogProps> = ({
  open,
  onClose,
  onEditComplete,
  onDeleteComplete,
  label
}: EditKeyDialogProps) => {
  const classes = useStyles();
  const [labelText, setLabelText] = useState<string>(label?.name ?? '');
  const [hasError, setHasError] = useState<boolean>(false);
  const {
    open: deleteConfirmationOpen,
    closeDialog: deleteConfirmationCloseDialog,
    openDialog: deleteConfirmationOpenDialog
  } = useDialogState();
  const {
    deleteCustomLabel,
    loading: deletingCustomLabel
  } = useDeleteCustomLabel();

  const onDeleteCustomLabel = useCallback(async () => {
    deleteConfirmationCloseDialog();
    const id = await deleteCustomLabel(label.id);
    onDeleteComplete(id);
  }, [
    deleteConfirmationCloseDialog,
    deleteCustomLabel,
    label.id,
    onDeleteComplete
  ]);

  const hintText = useMessage('customLabels.label');
  const errorMessage = useMessage('customLabels.duplicateKeyError');

  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setLabelText(event.target.value);
    setHasError(false);
  }, []);

  const {
    loading: updatingUserLabel,
    updateCustomLabel
  } = useUpdateCustomLabel();

  const onUpdateCustomLabel = useCallback(async () => {
    try {
      const updatedLabel = await updateCustomLabel({
        id: label.id,
        udpateName: labelText
      });
      onEditComplete(updatedLabel);
    } catch (e) {
      const errors = (e as ApolloError).graphQLErrors;

      errors.map((e) => {
        if (e.extensions?.code === 'INTERNAL_SERVER_ERROR') {
          setHasError(true);
        } else {
          console.error(e);
          throw e;
        }
      });
    }
  }, [label.id, labelText, onEditComplete, updateCustomLabel]);

  const getErrorProp = useMemo(() => {
    if (hasError)
      return {
        error: true,
        helperText: errorMessage
      };
  }, [errorMessage, hasError]);
  const onCancel = useCallback(() => {
    onClose();
  }, [onClose]);
  return (
    <>
      <Dialog open={open} data-testid="edit-key-dialog">
        <DialogTitle className={classes.title} data-testid="edit-key-title">
          <FormattedMessage id="customLabels.editKey" />
          <IconButton
            className={classes.delete}
            onClick={deleteConfirmationOpenDialog}
            disabled={deletingCustomLabel || updatingUserLabel}
          >
            {deletingCustomLabel ? (
              <CircularProgress size="24px" data-testid="deleting-key" />
            ) : (
              <DeleteIcon data-testid="delete-key" />
            )}
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            className={classes.edit}
            autoFocus
            size="small"
            label={hintText}
            fullWidth
            value={labelText}
            onChange={onChange}
            inputProps={inputProps}
            data-testid="edit-key-field"
            {...getErrorProp}
          />
        </DialogContent>
        <DialogActions className={classes.dialogAction}>
          <Button
            fullWidth={true}
            onClick={onCancel}
            className={classes.cancelButton}
            data-testid="edit-custom-cancel"
            color="error"
          >
            <FormattedMessage id="customLabels.cancel" />
          </Button>
          <ProgressButton
            loading={updatingUserLabel}
            onClick={onUpdateCustomLabel}
            className={classes.addButton}
            data-testid="edit-custom-addButton"
            disabled={!labelText || updatingUserLabel || deletingCustomLabel}
          >
            <FormattedMessage id="customLabels.updateButton" />
          </ProgressButton>
        </DialogActions>
      </Dialog>
      {deleteConfirmationOpen && (
        <DeleteComfirmationDialog
          open
          onClose={deleteConfirmationCloseDialog}
          onDelete={onDeleteCustomLabel}
        />
      )}
    </>
  );
};

const useDeleteConfirmationStyles = makeStyles((theme) => ({
  title: {
    color: theme.palette.grey[600],
    ...theme.typography.h6
  },
  content: {
    color: theme.palette.grey[800]
  },
  delete: {
    margin: theme.spacing(0.5, 1.5, 1),
    color: theme.palette.error.main
  }
}));

type DeleteConfirmationDialogProps = {
  open: boolean;
  onClose: () => void;
  onDelete: () => void;
};
const DeleteComfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  open,
  onClose,
  onDelete
}: DeleteConfirmationDialogProps) => {
  const classes = useDeleteConfirmationStyles();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      data-testid="key-delete-confirmation-dialog"
    >
      <DialogTitle className={classes.title}>
        <FormattedMessage id="customLabels.deleteConfirmationTitle" />
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Typography variant="body1">
          <FormattedMessage id="customLabels.deleteConfirmationMsg1" />
        </Typography>
        &nbsp;
        <Typography variant="body1">
          <FormattedMessage id="customLabels.deleteConfirmationMsg2" />
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          data-testid="key-delete-confirmation-dialog-cancel-btn"
        >
          <FormattedMessage id="customLabels.deleteConfirmationCancel" />
        </Button>
        <Button
          onClick={onDelete}
          className={classes.delete}
          data-testid="key-delete-confirmation-dialog-delete-btn"
        >
          <FormattedMessage id="customLabels.deleteConfirmationDelete" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
